import React, { useState } from 'react';
import { HandoffChecklistV2 } from '../handoff-checklist-v2/handoff-checklist-v2';
import { useWieHandoffMetrics } from './hooks/use-wie-handoff-metrics';
import { deriveOverallStatus } from './wie-handoff.helpers';
import {
  ChecklistMetricItem,
  ChecklistMetricStatus,
} from '../handoff-checklist-v2/handoff-checklist-v2.types';

interface WieHandoffProps {
  handoffId?: string;
  onCancel: () => void;
  onSaveAndClose: (notes: string) => void;
  onSubmit: (notes: string, metrics: ChecklistMetricItem[]) => void;
  onRefresh?: (refreshFn: () => void) => void;
  isSaving?: boolean;
  isSubmitting?: boolean;
  initialNotes?: string;
  initialMetrics?: ChecklistMetricItem[];
  readOnly?: boolean;
}

import { useEffect } from 'react';

export const WieHandoff = ({
  handoffId,
  onCancel,
  onSaveAndClose,
  onSubmit,
  onRefresh,
  isSaving = false,
  isSubmitting = false,
  initialNotes = '',
  initialMetrics = [],
  readOnly = false,
}: WieHandoffProps) => {
  const { metrics, setMetrics, refresh, isLoading, error } =
    useWieHandoffMetrics(handoffId);
  const [notes, setNotes] = useState<string>(initialNotes);

  // Pass refresh function up to parent
  useEffect(() => {
    onRefresh?.(refresh);
  }, [refresh, onRefresh]);

  // Initialize from loaded/saved metrics if present
  useEffect(() => {
    if (initialMetrics && initialMetrics.length > 0) {
      setMetrics(initialMetrics);
    }
  }, [initialMetrics, setMetrics]);

  const handleExceptionChange = (
    metricId: string,
    criteriaIndex: number,
    exception: string
  ) => {
    setMetrics((prevMetrics) =>
      prevMetrics.map((metric) => {
        if (metric.id !== metricId) {
          return metric;
        }

        const updatedCriteria = metric.criteria.map((criterion, idx) => {
          if (idx !== criteriaIndex) {
            return criterion;
          }

          // If exception is non-empty, change status to EXCEPTION_NOTED.
          // Otherwise, revert it back to BLOCKED
          const status = exception.trim()
            ? ChecklistMetricStatus.EXCEPTION_NOTED
            : ChecklistMetricStatus.BLOCKED;

          return {
            ...criterion,
            exception,
            status,
          };
        });

        const overallStatus = deriveOverallStatus(updatedCriteria.map((c) => c.status));

        return {
          ...metric,
          criteria: updatedCriteria,
          status: overallStatus,
        };
      })
    );
  };

  return (
    <HandoffChecklistV2
      metrics={metrics}
      title="Weave Insurance Eligibility"
      notes={notes}
      successMessage="Looks like all Weave Insurance Eligibility checks are good to go."
      onNotesChange={setNotes}
      onCancel={onCancel}
      onSaveAndClose={() => onSaveAndClose(notes)}
      onSubmit={() => onSubmit(notes, metrics)}
      onRefresh={refresh}
      isSaving={isSaving}
      isSubmitting={isSubmitting}
      isLoading={isLoading}
      error={error}
      readOnly={readOnly}
      showWhereToFix={false}
      onExceptionChange={handleExceptionChange}
    />
  );
};
